import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import QuestionCard from '../components/QuestionCard';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { testAPI, questionAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Target, ArrowRight, Home, Lock, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

const TestResults = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadResults = async () => {
    try {
      const testRes = await testAPI.getById(testId);
      setTest(testRes.data);

      const questionPromises = testRes.data.questions.map((qId) => questionAPI.getById(qId));
      const questionResults = await Promise.all(questionPromises);
      setQuestions(questionResults.map((res) => res.data));
    } catch (error) {
      toast.error('Failed to load results');
      navigate('/test/create');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="lg" text="Loading results..." />
      </Layout>
    );
  }

  const answerMap = test.answers?.reduce((acc, ans) => {
    acc[ans.question_id] = ans.selected_answer;
    return acc;
  }, {}) || {};

  // Calculate marks based on JEE/NEET scoring: +4 correct, -1 wrong, 0 not attempted
  const calculateMarks = () => {
    let correct = 0;
    let wrong = 0;
    let notAttempted = 0;

    questions.forEach((question) => {
      const userAnswer = answerMap[question.id];
      if (!userAnswer || userAnswer === '') {
        notAttempted++;
      } else if (userAnswer === question.correct_answer) {
        correct++;
      } else {
        wrong++;
      }
    });

    const totalMarks = (correct * 4) + (wrong * -1);
    const maxMarks = questions.length * 4;

    return {
      correct,
      wrong,
      notAttempted,
      totalMarks,
      maxMarks,
      percentage: questions.length > 0 ? ((totalMarks / maxMarks) * 100).toFixed(2) : 0
    };
  };

  const marks = calculateMarks();

  const performanceLevel = marks.percentage >= 80 ? 'Excellent' : marks.percentage >= 60 ? 'Good' : marks.percentage >= 40 ? 'Average' : 'Needs Improvement';
  const performanceColor = marks.percentage >= 80 ? 'text-green-500' : marks.percentage >= 60 ? 'text-blue-500' : marks.percentage >= 40 ? 'text-amber-500' : 'text-red-500';

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Results Header */}
        <Card className="p-8 md:p-12 mb-8 text-center bg-gradient-to-br from-primary/5 to-card rounded-3xl border border-border">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary rounded-3xl mb-6">
            <Trophy className="text-white" size={40} />
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">Test Complete</h1>
          <p className="text-lg text-muted-foreground mb-8">Here's your performance summary</p>

          {/* Marks Score Display */}
          <div className="mb-8">
            <div className="inline-flex items-baseline gap-2">
              <span className={`text-5xl md:text-6xl font-heading font-bold ${marks.totalMarks >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {marks.totalMarks}
              </span>
              <span className="text-2xl text-muted-foreground">/ {marks.maxMarks}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Marks Obtained</p>
          </div>

          <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto mb-8">
            <div className="p-4 md:p-6 bg-card rounded-2xl border border-border">
              <CheckCircle className="text-green-500 mx-auto mb-2" size={28} />
              <p className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-1">{marks.correct}</p>
              <p className="text-sm text-muted-foreground">Correct</p>
              <p className="text-xs text-green-600 font-semibold mt-1">+{marks.correct * 4} marks</p>
            </div>
            <div className="p-4 md:p-6 bg-card rounded-2xl border border-border">
              <XCircle className="text-red-500 mx-auto mb-2" size={28} />
              <p className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-1">{marks.wrong}</p>
              <p className="text-sm text-muted-foreground">Wrong</p>
              <p className="text-xs text-red-600 font-semibold mt-1">{marks.wrong * -1} marks</p>
            </div>
            <div className="p-4 md:p-6 bg-card rounded-2xl border border-border">
              <Target className="text-gray-500 mx-auto mb-2" size={28} />
              <p className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-1">{marks.notAttempted}</p>
              <p className="text-sm text-muted-foreground">Skipped</p>
              <p className="text-xs text-gray-600 font-semibold mt-1">0 marks</p>
            </div>
          </div>

          <div className="mb-6">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-card rounded-2xl border border-border">
              <Trophy className={performanceColor} size={24} />
              <div className="text-left">
                <p className="text-xs text-muted-foreground">Percentage</p>
                <p className={`text-2xl font-heading font-bold ${performanceColor}`}>{marks.percentage}%</p>
              </div>
            </div>
          </div>

          <Badge className={`mb-6 px-4 py-2 text-sm ${
            marks.percentage >= 80 ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 
            marks.percentage >= 60 ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 
            marks.percentage >= 40 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 
            'bg-red-500/10 text-red-600 dark:text-red-400'
          }`}>
            {performanceLevel}
          </Badge>

          {/* Scoring System Info */}
          <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl max-w-xl mx-auto">
            <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">📊 Marking Scheme</p>
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="text-green-600 font-bold">+4</span> Correct
              </span>
              <span className="text-gray-300">•</span>
              <span className="flex items-center gap-1">
                <span className="text-red-600 font-bold">-1</span> Wrong
              </span>
              <span className="text-gray-300">•</span>
              <span className="flex items-center gap-1">
                <span className="text-gray-600 font-bold">0</span> Skipped
              </span>
            </div>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/test/create')}
              className="rounded-full bg-primary hover:bg-primary/90 text-white px-8"
            >
              Take Another Test
              <ArrowRight className="ml-2" size={18} />
            </Button>
            {isAuthenticated ? (
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="rounded-full border-2 border-border px-8"
              >
                <Home className="mr-2" size={18} />
                Dashboard
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate('/auth?mode=register')}
                className="rounded-full border-2 border-border px-8"
              >
                <Sparkles className="mr-2" size={18} />
                Sign Up Free
              </Button>
            )}
          </div>
        </Card>

        {/* Premium Prompt for Non-Authenticated Users */}
        {!isAuthenticated && (
          <Card className="p-6 mb-8 bg-amber-500/10 border border-amber-500/30 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Lock className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-foreground mb-1">Unlock Full Analytics</h3>
                <p className="text-sm text-muted-foreground">Sign up to track your progress, see detailed analysis, and save your test history.</p>
              </div>
              <Button onClick={() => navigate('/auth?mode=register')} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full">
                Sign Up Free
              </Button>
            </div>
          </Card>
        )}

        {/* Basic Results - Visible to all */}
        <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
          {isAuthenticated ? 'Solutions & Explanations' : 'Quick Review'}
        </h2>
        
        {!isAuthenticated && (
          <p className="text-sm text-muted-foreground mb-4">
            Sign up to see detailed explanations and track your weak areas.
          </p>
        )}

        <div className="space-y-6">
          {questions.map((question, index) => {
            const userAnswer = answerMap[question.id];
            const isCorrect = userAnswer === question.correct_answer;
            
            return (
              <Card key={question.id} className="p-6 bg-card rounded-2xl border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isCorrect ? 'bg-green-500' : 'bg-red-500'
                  } text-white font-bold`}>
                    {index + 1}
                  </div>
                  <span className={`text-sm font-semibold ${
                    isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {isCorrect ? 'Correct' : 'Incorrect'}
                  </span>
                </div>
                
                <QuestionCard
                  question={question}
                  selectedAnswer={userAnswer}
                  showAnswer={true}
                  disabled={true}
                />
                
                {/* Show explanation only for authenticated users */}
                {!isAuthenticated && question.explanation && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-xl flex items-center gap-3">
                    <Lock size={18} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Sign up to see the full explanation</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA for non-authenticated */}
        {!isAuthenticated && (
          <Card className="p-8 mt-8 text-center bg-primary/5 rounded-3xl border border-primary/20">
            <Sparkles className="text-primary mx-auto mb-4" size={40} />
            <h3 className="text-xl font-bold text-foreground mb-2">Want to Track Your Progress?</h3>
            <p className="text-muted-foreground mb-6">Create an account to save tests, track mistakes, and get recommendations.</p>
            <Button onClick={() => navigate('/auth?mode=register')} className="bg-primary text-white rounded-full px-8">
              Create Free Account
            </Button>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default TestResults;
